import accountsPayableReceivable from "./accounts-payable-receivable.json";
import bills from "./bills.json";
import finExManagerialReport from "./finex-managerial-report.json";
import planOfAccounts from "./plan-of-accounts.json";
import customerAndSupplier from "./customer-and-supplier.json";
import stockTransferHistory from "./stock-transfer-history.json";
import { CONCILIATION_EN_US } from "#pages/backoffice/conciliation/translations/conciliation-en-us";
import eventConference from "./event-conference.json";

export const backofficeEN = {
	eventConference,
	accountsPayableReceivable,
	stockTransferHistory,
	bills,
	cashFlow: {
		pageTitle: "Cash Flow",
		pageParagraph:
			"Every company needs a cash flow to track its inflows and outflows. A manager who knows their financial flow is aware of what comes in and goes out by day, week and month. Having a tool like this and using it is extremely important for decision-making and for knowing where the business is heading. Currently, our cash flow takes into account the due date of the bills entered in accounts payable and receivable.",
		tooltipMessage:
			"The category lines of level {{level}} display the sum of all the levels grouped within it.",
		fileName: "cash-flow",
		topActions: {
			lblAccountName: "Search by Account",
			plhAccountName: "Enter the account name",
			lblDate: "Filter by Expiration",
			btnPdf: "Export to PDF",
			btnExcel: "Export to Excel",
		},
		totalizers: {
			income: "Total Income",
			costs: "Total Costs",
			expenses: "Total Expenses",
			balance: "Total Income, Costs and Expenses",
		},
		rowTotal: "Period Balance",
		rowHead: "Initial Balance",
		rowTail: "Balance for the Day",
		viewPerDay: "View per day",
		viewPerMonth: "View per month",
		columns: {
			income: "Revenues",
			costs: "Costs",
			expenses: "Expenses",
			level: "Level",
			name: "Name",
			day: "Day",
			month: "Month",
		},
		bills: {
			searchAccountPlaceholder: "Enter the account name",
			dueDateFilter: "Filter by due date",
			exportExcel: "Export to excel",
			emptyText: "No bills found for the selected period.",
			cards: {
				receipts: "Revenues",
				expenses: "Expenses",
				finalBalance: "Final balance for the period",
			},
			columns: {
				dueDate: "Due date",
				description: "Description",
				counterparty: "Customer/Supplier",
				account: "Bank account",
				category: "Category",
				status: "Status",
				value: "Amount",
			},
			status: {
				received: "Received",
				paid: "Paid",
				pending: "Open",
				overdue: "Overdue",
			},
			openingBalance: "Opening balance",
			dayBalance: "Balance for {{date}}",
			periodBalance: "Period balance",
		},
	},
	costCenter: {
		costCenter: "Cost centers",
		addCostCenter: "Add cost center",
		editCostCenter: "Edit cost center",
		namePlaceholder: "Cost center name",
		name: "Name",
		action: "Actions",
	},
	planOfAccounts,
	bankAccounts: {
		titlePage: "Bank Accounts",
		addAccount: "Register Account",
		columnsTable: {
			name: "Name",
			bank: "Bank",
			agency: "Agency",
			purpose: "Account Purpose",
			actions: "Actions",
			account: "Account",
			cpfOrCnpj: "CPF/CNPJ",
			ownerName: "Account Holder",
			currentBalance: "Current Balance",
			initialBalance: "Initial balance",
		},
		form: {
			newNameBankAccount: "Account Name",
			bank: "Banking Institution",
			agency: "Agency Code",
			purpose: "Account Purpose",
			account: "Current Account with Digit",
			cpfOrCnpj: "CPF/CNPJ",
			ownerName: "Account Holder",
			addAccount: "Add Bank Account",
			namePlaceholder: "Enter a name for the account",
			bankPlaceholder: "Search for institution",
			purposePlaceholder: "Search by account type",
			selectPurpose: "Select account purpose",
			followInstructions: "Follow the instructions to continue",
			accessBankAccount:
				"To continue, access your internet banking and check if it is possible to download your statement in OFX format.",
			verifyBankAccount:
				"If the bank does not export this file format, click Finish to proceed without validation.",
			successContent:
				"The information imported from OFX is in accordance with the information entered in the previous step",
			inconsistentContent:
				"Some information imported from OFX does not match the information entered in the previous step. When continuing, we will replace the information entered with the information imported.",
			emptyBankData:
				"Some information required for account validation is missing from the selected OFX file. Click continue to complete the registration without validation.",
			imported: "Imported",
			registered: "Registered",
			titlesContentVerifield: {
				success: "Verified Information",
				inconsistency: "Conflicting Information",
				emptyBankData: "Missing information",
			},
			ofxTitles: {
				upload: "Click or drag the OFX file",
				file: "OFX File",
			},
			incompatibleFile: "Incompatible file",
			purposeList: {
				inHouseFlow: "Cash (Petty Cash)",
				creditCard: "Credit Card",
				virtualWallet: "Virtual Wallet",
				applicationAccount: "Investment Account",
				checkingAccount: "Checking Account",
				savingsAccount: "Savings Account",
			},
			accountPlaceholder: "Enter the account",
			agencyPlaceholder: "Enter the code",
			cpfPlaceholder: "000.000.000-00",
			ownerNamePlaceholder: "Account holder's name",
			unlink: "Unlink Bank Account",
			linkBankAccount: "Link Bank Account",
			defaultAccount: "Set as Main Account:",
			defaultAccountTooltip:
				"The main account is used to automatically fill forms that require this information.",
			default: "Default",
			initialBalance: "Initial Balance in $",
			initialBalanceDate: "Initial Balance Date",
			balancePlaceholder: "0.00",
			balanceDatePlaceholder: "mm/dd/yyyy",
			saveChanges: "Save Changes",
			errors: {
				required: "Required Field",
				name: "Enter a valid name",
				account: "Enter a valid account number",
				agency: "Enter a valid agency number",
				cpf: "Invalid CPF",
				cnpj: "Invalid CNPJ",
				formatInvalid: "This file format is not supported",
			},
		},
		confirmModal: {
			editInfo: "Edit Information",
			cancelMessage:
				"You have not saved the changes made. Do you still want to continue?",
			cancelButton: "Exit without saving",
			confirmButton: "Save Changes",
			deleteAccount: "Delete Account",
			deleteMessage:
				"Deleting the account is a definitive action. Do you wish to proceed?",
			deleteTransferError:
				"This account cannot be deleted because it is linked to recorded payments or receipts.",
			deleteMainError:
				"This account cannot be deleted because it is registered as the main account.",
			updateMainAccount: "Change Main Account",
			updateMainAccountMessage:
				"There is already a main account registered in this location. If you proceed, this account will be replaced. Do you still wish to continue?",
			confirmUpdate: "Change Account",
			deactivateTitle: "Deactivate account",
			deactivateMessage:
				"Are you sure you want to deactivate the account {{name}}? The account will not be deleted. The transaction history is preserved and you can reactivate it at any time.",
			deactivateConfirm: "Deactivate account",
		},
		subtitlePage:
			"Register, manage and track the transactions of your main bank accounts",
		tabs: {
			active: "Active accounts",
			inactive: "Deactivated accounts",
		},
		emptyTab: "No accounts in this tab",
		comingSoon: "Coming soon",
		zigCard: {
			title: "ZIG Account",
			viewExtract: "View statement",
			platform: "Available on platform",
			withdraw: "Available for withdrawal",
			anticipation: "Available for anticipation",
			future: "Future entries",
		},
		card: {
			viewTransactions: "View transactions",
			agencyAbbr: "Branch",
			accountAbbr: "Acc.",
		},
		migration: {
			banner: {
				title: "Update the balance of your bank accounts",
				description:
					"Your bank accounts have a new look. For your balances to show correctly in the new experience, update your accounts with the most recent balance.",
			},
			badge: "Update balance",
			accountWarningTooltip: "Open the account details and update the balance",
		},
		switch: {
			activate: "Activate account",
			deactivate: "Deactivate account",
		},
		menu: {
			ariaLabel: "Account actions",
			editData: "Edit account data",
			updateBalance: "Update balance",
		},
		emptyStateTitle: "No bank account registered",
		emptyStateSubtitle: "Add your first bank account to get started",
		notification: {
			deleteAccount: "Account successfully deleted",
			createAccount: "Account successfully created",
			updateAccount: "Account successfully edited",
			accountActivated: "Account successfully activated",
			accountDeactivated: "Account successfully deactivated",
		},
	},
	reports: {
		noComponent: "Wait, it will be available soon",
		reportNames: {
			expense: {
				perDescripion: "Expense by Description",
				perDay: "Expense per Day",
				perType: "Expense by Type",
				perCategory: "Expense by Category",
				perCostCenter: "Expense by Cost Center",
				perHistory: "Historical Expense",
				perAnything: "Expense Paid to...",
			},
			receipts: {
				perDescripion: "Receipts by Description",
				perDay: "Receipts per Day",
				perType: "Receipts by Type",
				perCategory: "Receipts by Category",
				perCostCenter: "Receipts by Cost Center",
				perHistory: "Historical Receipts",
				perAnything: "Receipts Received from...",
			},
			cashFlow: {
				extract: "Extract",
				expensses: "Expenses/Receipts",
				history: "Historic",
				demonstrative: "Demonstrative",
			},
		},
		graphs: {
			totalExpenses: "Total expenses",
			totalReceipts: "Total receipts",
			finalBalance: "Final balance",
			periodBalance: "Period balance",
			previousBalance: "Previous balance",
			date: "Date",
			description: "Description",
			category: "Category",
			receipts: "Receipt",
			expenses: "Expenses",
			dateCaps: "DATE",
			receiptCaps: "RECEIPT",
			expenseCaps: "EXPENSE",
			valueCaps: "VALUE",
			totalCaps: "TOTAL",
			type: "Type",
			costCenterCaps: "COST CENTER",
			tax: "Tax",
			receipt: "Receipt",
			expense: "Expense",
			result: "Income before tax",
			profit: "Profit in exercise",
			descriptionCaps: "DESCRIPTION",
			value: "Value",
			balance: "Balance",
			period: "Period",
			until: "until",
			totalReceived: "Total Received",
			totalPaid: "Total paid",
			finalBalanceExtract: "Ending balance",
			periodBalanceExtract: "Balance in the period",
			previousBalanceExtract: "Previous balance",
			supplierCaps: "SUPPLIER",
			typeCaps: "TYPE",
		},
	},
	billType: {
		categories: "Categories",
		addCategory: "Add account category",
		addType: "Add account type",
		createCategory: "Create account category",
		editCategory: "Edit account category",
		categoryName: "Account category name",
		createTypeIn: "Create account type in {{categoryName}}",
		editTypeIn: "Edit account type in {{categoryName}}",
		createType: "Create account type",
		editType: "Edit account type",
		typeName: "Account type name",
		name: "Name",
		actions: "Actions",
	},
	finExManagerialReport,
	customerAndSupplier,
	viewLevel: "View level {{level}}",
	conciliation: CONCILIATION_EN_US,
};
